// SocketContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { SYNC_STAGES, getStageProgress, ACTIVE_SYNC_STAGES } from '../constants/syncStages';
import { debugLog } from '../utils/debugLog';

const SocketContext = createContext();

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children, jwt }) => {
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState(null);
  const [syncState, setSyncState] = useState(() => {
    // Initialize sync state from sessionStorage (not sensitive data)
    try {
      const saved = sessionStorage.getItem('syncState');
      return saved ? JSON.parse(saved) : {
        isActive: false,
        status: 'idle',
        currentStage: null,
        currentMessage: '',
        appName: '',
        progress: 0,
        history: [],
        startTime: null
      };
    } catch {
      return {
        isActive: false,
        status: 'idle',
        currentStage: null,
        currentMessage: '',
        appName: '',
        progress: 0,
        history: [],
        startTime: null
      };
    }
  });

  // Function to update sync state
  const updateSyncState = (parsedMessage) => {
    setSyncState(prevState => {

      const newHistory = [...prevState.history, {
        id: Date.now(),
        stage: parsedMessage.stage,
        message: parsedMessage.message,
        timestamp: parsedMessage.timestamp ? new Date(parsedMessage.timestamp) : new Date(),
        error: parsedMessage.error,
        ...parsedMessage
      }].slice(-20); // Keep last 20 messages

      let newState = {
        ...prevState,
        currentStage: parsedMessage.stage,
        currentMessage: parsedMessage.message,
        appName: parsedMessage.app_name || prevState.appName,
        progress: getStageProgress(parsedMessage.stage) || prevState.progress,
        history: newHistory
      };

      if (parsedMessage.stage === SYNC_STAGES.INIT) {
        newState = {
          ...newState,
          isActive: true,
          status: 'syncing',
          startTime: new Date(),
          history: [] // Clear previous history
        };
      } else if (!prevState.isActive && ACTIVE_SYNC_STAGES.includes(parsedMessage.stage)) {
        // User switched accounts mid-sync - restore sync state
        newState = {
          ...newState,
          isActive: true,
          status: 'syncing',
          startTime: new Date(), // Approximate start time since we don't know the actual start
          history: [newHistory[newHistory.length - 1]] // Start fresh history with this message
        };
      } else if (parsedMessage.stage === SYNC_STAGES.COMPLETE) {
        newState = {
          ...newState,
          status: 'complete',
          progress: 100
        };
        
        // Clear active sync state after 3 seconds but preserve history
        setTimeout(() => {
          setSyncState(prevState => {
            const clearedState = {
              ...prevState, // Preserve all existing state
              isActive: false,
              status: 'idle',
              currentStage: null,
              currentMessage: '',
              progress: 0,
              startTime: null
              // Keep history and appName
            };
            sessionStorage.setItem('syncState', JSON.stringify(clearedState));
            return clearedState;
          });
        }, 3000);
      } else if (parsedMessage.stage === SYNC_STAGES.ERROR) {
        newState = {
          ...newState,
          status: 'error'
        };
        
        // Clear active sync state after 5 seconds but preserve history
        setTimeout(() => {
          setSyncState(prevState => {
            const clearedState = {
              ...prevState, // Preserve all existing state
              isActive: false,
              status: 'idle',
              currentStage: null,
              currentMessage: '',
              progress: 0,
              startTime: null
              // Keep history and appName
            };
            sessionStorage.setItem('syncState', JSON.stringify(clearedState));
            return clearedState;
          });
        }, 5000);
      }

      // Save to localStorage
      sessionStorage.setItem('syncState', JSON.stringify(newState));
      return newState;
    });
  };

  // Ensure sync state is loaded from localStorage on component mount (only once)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('syncState');
      if (saved) {
        const parsedState = JSON.parse(saved);
        // Only load if we don't already have history
        setSyncState(prevState => {
          if (!prevState.history || prevState.history.length === 0) {
            return { ...parsedState, isActive: false }; // Ensure not active on load
          }
          return prevState;
        });
      }
    } catch (error) {
      // Silently handle localStorage errors
    }
  }, []); // Run only once on mount

  // Only clear sync state on page unload if user is logging out (not just navigating)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear sync state on normal page navigation
      // Only clear if there's no JWT (user is logging out)
      if (!jwt) {
        localStorage.removeItem('syncState');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [jwt]);

  // Helper function to decode JWT and check if user is in account context
  const hasAppConnection = (token) => {
    if (!token) return false;
    try {
      // Decode JWT payload (without verification since we're just checking structure)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !!(payload.appConnection && payload.appConnection !== '');
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!jwt) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      setMessage(null);
      // Clear sync state when user logs out
      const clearedState = {
        isActive: false,
        status: 'idle',
        currentStage: null,
        currentMessage: '',
        appName: '',
        progress: 0,
        history: [],
        startTime: null
      };
      setSyncState(clearedState);
      localStorage.removeItem('syncState');
      return;
    }

    // Only connect socket if user is in an account context (has appConnection)
    if (!hasAppConnection(jwt)) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      setMessage(null);
      // When not in account context, preserve history but clear active sync
      setSyncState(prevState => {
        const preservedState = {
          ...prevState,
          isActive: false,
          status: 'idle',
          currentStage: null,
          currentMessage: '',
          progress: 0,
          startTime: null
          // Keep history and appName for display purposes
        };
        sessionStorage.setItem('syncState', JSON.stringify(preservedState));
        return preservedState;
      });
      return;
    }

    // Check if we're switching to a different app/account
    // const currentSyncState = JSON.parse(localStorage.getItem('syncState') || '{}');
    
    try {
      if (jwt) {
        // const payload = JSON.parse(atob(jwt.split('.')[1]));
        // currentAppFromJWT = payload.appConnection || payload.app_connection_id;
      }
    } catch (error) {
      console.warn('Error parsing JWT for app connection:', error);
    }
    
    // Only clear history if we have a previous app and we're switching to a completely different one
    // Be more conservative - only clear if we're certain it's a different app
    const shouldClearHistory = false; // Disable aggressive clearing for now
    
    
    if (shouldClearHistory) {
      // Clear sync state when switching accounts/apps
      const clearedState = {
        isActive: false,
        status: 'idle',
        currentStage: null,
        currentMessage: '',
        appName: '',
        progress: 0,
        history: [],
        startTime: null
      };
      setSyncState(clearedState);
      sessionStorage.setItem('syncState', JSON.stringify(clearedState));
    } else {
      // Always preserve history and just ensure sync is not active
      setSyncState(prevState => {
        const preservedState = {
          ...prevState,
          isActive: false,
          status: 'idle',
          currentStage: null,
          currentMessage: '',
          progress: 0,
          startTime: null
          // Preserve history, appName, and all other data
        };
        sessionStorage.setItem('syncState', JSON.stringify(preservedState));
        return preservedState;
      });
    }

    const socketInstance = io(process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:8080', {
      transports: ['websocket'],
    });

    setSocket(socketInstance);

    socketInstance.on('connect', () => {
      debugLog('[Socket] - Connected to server');
      socketInstance.emit('authenticate', { token: jwt });
    });

    socketInstance.on('authenticated', (data) => {
      debugLog('[Socket] - Authenticated successfully');
    });

    socketInstance.on('unauthorized', (err) => {
      console.error('socket: Authentication failed', err);
    });

    socketInstance.on('message', (msg) => {
      debugLog('[Socket] - Received message:', msg);
      setMessage(msg);
      
      // Update sync state for persistence
      if (msg && msg.message) {
        try {
          const parsedMessage = JSON.parse(msg.message);
          if (parsedMessage.stage) {
            updateSyncState(parsedMessage);
          }
        } catch {
          // Not a structured sync message, ignore for sync state
        }
      }
      
      socketInstance.emit('ack', { received: true, originalMessage: msg });
    });

    return () => {
      socketInstance.off('authenticated');
      socketInstance.off('unauthorized');
      socketInstance.off('message');
      socketInstance.disconnect();
      setSocket(null);
      setMessage(null);
    };
    // IMPORTANT: Do NOT include 'socket' in the dependency array! 
    // This would cause an endless loop since setSocket(null) is called in the cleanup.
    // Only depend on 'jwt' - the effect should re-run when the JWT token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  return (
    <SocketContext.Provider value={{ socket, message, setMessage, syncState, setSyncState }}>
      {children}
    </SocketContext.Provider>
  );
};
